/**
 * Mesi di validità della gift card, come li racconta la copy del portale.
 *
 * NON è la scadenza vera: quella la decide il backend e arriva per card in
 * `tsExpire` (vedi build_order), che è il solo dato da mostrare su un ordine
 * emesso. Questo valore serve alle pagine di vendita, dove una card ancora non
 * esiste e c'è solo una promessa da fare all'utente: sta qui perché la ripetono
 * home, checkout e condizioni, e finora era scritto a mano in nove punti.
 */
export const VALIDITY_MONTHS = 12;

/**
 * Promessa di validità stampata sulle card in vetrina.
 *
 * Derivata da VALIDITY_MONTHS e non ricomposta nei componenti: la ripetono il
 * carousel della home, la card blu del riepilogo e quella del login-gate, e
 * devono dire la stessa cosa con le stesse parole.
 */
export const VALIDITY_LABEL = `Valida ${VALIDITY_MONTHS} mesi`;

/**
 * Label della CTA che porta all'ordine.
 *
 * L'importo compare solo se c'è: un prodotto senza tagli in catalogo non fa
 * preselezionare niente ad `amount-picker`, e la CTA non deve annunciare un
 * prezzo che non esiste. Sta qui perché la home ha due CTA per lo stesso ordine
 * — la colonna di sinistra e la barra di riepilogo — che erano già divergenti.
 * Il controllo è su falsy e non su `null` perché nemmeno uno zero è un prezzo
 * da annunciare, ed è lo stesso metro della guardia del checkout.
 *
 * @param {Number|null} amount taglio scelto, in euro
 * @returns {String} testo della CTA
 */
export function checkout_cta_label(amount) {
  if (!amount) return `Procedi all'ordine`;
  return `Procedi all'ordine - €${amount}`;
}

/**
 * Cartella degli SVG dei marchi di prodotto, dentro `public`.
 *
 * Il path passa da `BASE_URL` e non è assoluto: `public/` non attraversa webpack
 * e su UAT il `publicPath` è dinamico (`/testing/{brand}/{branch}/`), quindi un
 * `/assets/...` scritto a mano finirebbe in 404.
 */
const PRODUCT_LOGOS_PATH = `${process.env.BASE_URL}assets/images/livegift/products`;

/**
 * Contenuti di vetrina delle gift card, indicizzati per `productCode`.
 *
 * Quali card esistono, con che tagli e su quale portale si spendono lo dice il
 * catalogo (`gc_catalog.php`): non è roba di questo file. Dei testi però il
 * contratto porta solo `productName` e `description`, e categoria, dettaglio e
 * loghi non ci sono affatto (è un open point di CAN-366), quindi la copy sta qui.
 *
 * **Quali di queste card siano davvero in vendita non lo decide questo file**: il
 * backend filtra il catalogo su un'allow-list di `productCode`, la variabile di
 * store `gvGiftcardsEnabled` (`admins/variables`), che il nodo
 * `customizations.giftcardsEnabled` del token SSO del partner può sostituire in
 * blocco. Qui stanno i contenuti di tutte, comprese quelle oggi spente: non c'è
 * niente da commentare o togliere quando una card esce dall'allow-list.
 *
 * Le chiavi sono i `productCode` veri. Una card abilitata di là ma senza la sua
 * voce qui si vede lo stesso, col nome interno del backend e senza categoria né
 * dettaglio: `build_products` ripiega, non salta.
 *
 * `logo` / `logo_white` sono path a SVG statici sotto `public`, non type del
 * componente <logo>: quel registro tiene i loghi del *site* (in `logo_headline`
 * "headline" è il ruolo, il marchio in testa alla pagina), mentre qui serve il
 * marchio del portale su cui la card si spende. Il file si chiama come il
 * `productCode` senza `gc-`, così la chiave di questa mappa dice già quale asset
 * cercare; la versione `_white` è per le card su fondo blu (selected-gift-card e
 * testata di order-recap).
 *
 * `description` e `details` contengono markup: si renderizzano con v-html, come
 * fa liveflight/blocks/accordion.vue col contenuto degli item.
 */
const PRODUCT_CONTENTS = {

  'gc-vadobay-live': {
    name: 'VadoBay livebooking Powered by Expedia',
    category: 'hotel e soggiorni',
    logo: `${PRODUCT_LOGOS_PATH}/vadobay-live.svg`,
    logo_white: `${PRODUCT_LOGOS_PATH}/vadobay-live-white.svg`,
    description: 'Ideale per: Hotel, resort e case vacanze nel mondo con tariffe dedicate.',
    details: 'Sblocca una selezione premium del catalogo Expedia Group a tariffe esclusive. Scegli tra oltre 400.000 tra boutique hotel, grandi resort e appartamenti in qualsiasi destinazione globale, con la certezza di un\'infrastruttura tecnologica solida e affidabile.',
  },

  'gc-liveflight': {
    name: 'fly by VadoBay (Prenotazioni voli)',
    category: 'voli di linea e low cost',
    logo: `${PRODUCT_LOGOS_PATH}/liveflight.svg`,
    logo_white: `${PRODUCT_LOGOS_PATH}/liveflight-white.svg`,
    description: 'Ideale per: voli di linea e low cost.',
    details: 'Metti le ali al tuo credito welfare con un motore di ricerca voli completo. Con questa Gift Card puoi prenotare sia i voli delle principali compagnie low cost sia i voli di linea delle migliori compagnie di bandiera per raggiungere qualsiasi destinazione nel mondo.',
  },

  'gc-vacanze-vadobay': {
    name: 'vacanze by VadoBay (Special partner Alpitour World)',
    category: 'pacchetti viaggio, tours',
    logo: `${PRODUCT_LOGOS_PATH}/vacanze-vadobay.svg`,
    logo_white: `${PRODUCT_LOGOS_PATH}/vacanze-vadobay-white.svg`,
    description: 'Ideale per: Pacchetti "tutto incluso" e villaggi turistici di qualità.',
    details: 'La scelta perfetta per chi cerca il relax assoluto. Sfrutta il tuo credito per prenotare pacchetti viaggio completi (volo + hotel + trasferimenti) oppure scegli il solo soggiorno nei migliori resort e club Alpitour World, per viaggiare autonomamente.',
  },

  'gc-vadobay-booking': {
    name: 'VadoBay in partnership with Booking.com',
    category: 'hotel e soggiorni',
    logo: `${PRODUCT_LOGOS_PATH}/vadobay-booking.svg`,
    logo_white: `${PRODUCT_LOGOS_PATH}/vadobay-booking-white.svg`,
    description: 'Ideale per: La massima scelta possibile di strutture, hotel e appartamenti.',
    details: 'Accedi al catalogo di soggiorni più grande e capillare al mondo. Questa Gift Card ti dà la massima libertà di scelta: dai piccoli B&B di charme ai grandi hotel cittadini, fino alle case vacanze per tutta la famiglia. Trovi la sistemazione perfetta in qualunque angolo del pianeta.',
  },
};

/**
 * Contenuti di vetrina di una singola card, per `productCode`.
 *
 * Serve a chi mostra una card senza passare dal catalogo: al visitatore senza
 * sessione il catalogo non si chiede (vedi la home), quindi lo store è vuoto e
 * questa mappa è l'unica fonte. La usa `login-gate`.
 *
 * @param {string} product_code `productCode` della card
 * @returns {Object} i contenuti della card, oggetto vuoto se non c'è la voce
 */
export function product_contents(product_code) {
  return PRODUCT_CONTENTS[product_code] || {};
}

/**
 * Monta i prodotti da mostrare a partire dalle entry del catalogo.
 *
 * Il catalogo comanda su esistenza, tagli e portale di redemption. Su nome e
 * descrizione vincono invece i contenuti locali: di là `productName` è un nome
 * interno ("gift card vadobay eps") e `description` è vuota, quindi prenderli
 * peggiorerebbe la pagina. I campi del catalogo restano come ripiego per una
 * card che comparisse prima della sua copy: meglio un nome brutto che niente.
 * Quando quei testi saranno affidabili basta invertire i due `||`.
 *
 * @param {Array} catalog `giftcardCatalog` della response di `catalog`
 * @returns {Array} prodotti pronti per la vista
 */
export function build_products(catalog) {
  if (!Array.isArray(catalog)) return [];

  return catalog.map((entry) => {
    const contents = product_contents(entry.productCode);

    return {
      ...contents,
      id: entry.productCode,
      name: contents.name || entry.productName || '',
      description: contents.description || entry.description || '',
      // tagli vendibili per questo prodotto, in euro
      amounts: Array.isArray(entry.valueNominal) ? entry.valueNominal : [],
      // portale su cui la card si spende, NON lo store che la vende
      portal: entry.portal || '',
    };
  });
}
